import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { VyvLipSyncDebug } from "@/hooks/useLipSync";

// ─────────────────────────────────────────────────────────────────────────────
// LipSyncCalibrationHUD — DEV-ONLY overlay to calibrate the viseme lip-sync.
//
// It reads/writes the SAME `window.__VYV_LIPSYNC` bridge that
// src/hooks/useLipSync.ts consumes each frame, so every slider takes effect LIVE
// on device with NO rebuild. Use it to dial the output-latency phase offset
// until the mouth locks to the audio on the Live (bust) screen.
//
// Gated twice:
//   1. Dev flag — import.meta.env.DEV OR localStorage 'vyv_lipsync_hud' === '1'.
//   2. Mount site — MirrorChat only renders it on the Live screen.
// In production both the flag is false AND the component returns null, so it adds
// nothing to the layout. Rendered through a portal to <body> so the Live screen's
// `pointer-events-none` stage layer can't swallow the slider drags.
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULTS = {
  phaseMs: 160,
  releaseTauMs: 50,
  maxJaw: 0.4,
  intensity: 0.8,
} as const;

function hudEnabled(): boolean {
  try {
    if (import.meta.env.DEV) return true;
    return (
      typeof localStorage !== "undefined" &&
      localStorage.getItem("vyv_lipsync_hud") === "1"
    );
  } catch {
    return false;
  }
}

function getBridge(): VyvLipSyncDebug | undefined {
  if (typeof window === "undefined") return undefined;
  return (window.__VYV_LIPSYNC ??= {});
}

function numOr(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function setBridge<K extends keyof VyvLipSyncDebug>(key: K, value: VyvLipSyncDebug[K]): void {
  const b = getBridge();
  if (b) b[key] = value;
}

// Outer component holds NO hooks so production is a guaranteed early null with
// no hook-order surprises; the panel (with hooks) only mounts when enabled.
export default function LipSyncCalibrationHUD() {
  if (!hudEnabled()) return null;
  return <HudPanel />;
}

function HudPanel() {
  const bridge = getBridge();

  const [phaseMs, setPhaseMs] = useState<number>(() => numOr(bridge?.phaseMs, DEFAULTS.phaseMs));
  const [releaseTauMs, setReleaseTauMs] = useState<number>(() =>
    numOr(bridge?.releaseTauMs, DEFAULTS.releaseTauMs),
  );
  const [maxJaw, setMaxJaw] = useState<number>(() => numOr(bridge?.maxJaw, DEFAULTS.maxJaw));
  const [intensity, setIntensity] = useState<number>(() => numOr(bridge?.intensity, DEFAULTS.intensity));
  const [collapsed, setCollapsed] = useState(false);

  // Read-only telemetry the hook writes (device output latency + applied phase).
  const [measuredMs, setMeasuredMs] = useState<number | null>(null);
  const [effectiveMs, setEffectiveMs] = useState<number | null>(null);

  // Seed the bridge with the initial slider values so the hook uses them even
  // before the first drag.
  useEffect(() => {
    setBridge("phaseMs", phaseMs);
    setBridge("releaseTauMs", releaseTauMs);
    setBridge("maxJaw", maxJaw);
    setBridge("intensity", intensity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll telemetry twice a second.
  useEffect(() => {
    const id = window.setInterval(() => {
      const b = getBridge();
      setMeasuredMs(
        b && typeof b.measuredOutputLatencyMs === "number" ? b.measuredOutputLatencyMs : null,
      );
      setEffectiveMs(b && typeof b.effectivePhaseMs === "number" ? b.effectivePhaseMs : null);
    }, 500);
    return () => window.clearInterval(id);
  }, []);

  const reset = () => {
    setPhaseMs(DEFAULTS.phaseMs);
    setBridge("phaseMs", DEFAULTS.phaseMs);
    setReleaseTauMs(DEFAULTS.releaseTauMs);
    setBridge("releaseTauMs", DEFAULTS.releaseTauMs);
    setMaxJaw(DEFAULTS.maxJaw);
    setBridge("maxJaw", DEFAULTS.maxJaw);
    setIntensity(DEFAULTS.intensity);
    setBridge("intensity", DEFAULTS.intensity);
  };

  const panel = (
    <div style={styles.root}>
      <div style={styles.header}>
        <span style={styles.title}>lip-sync · calib</span>
        <div>
          <button style={styles.btn} onClick={reset}>reset</button>
          <button style={styles.btn} onClick={() => setCollapsed((c) => !c)}>
            {collapsed ? "▣" : "—"}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          <Slider
            label="Phase offset"
            unit="ms"
            value={phaseMs}
            min={0}
            max={400}
            step={5}
            onChange={(v) => {
              setPhaseMs(v);
              setBridge("phaseMs", v);
            }}
          />
          <Slider
            label="Release τ"
            unit="ms"
            value={releaseTauMs}
            min={20}
            max={150}
            step={5}
            onChange={(v) => {
              setReleaseTauMs(v);
              setBridge("releaseTauMs", v);
            }}
          />
          <Slider
            label="Max jaw"
            value={maxJaw}
            min={0.2}
            max={0.7}
            step={0.02}
            decimals={2}
            onChange={(v) => {
              setMaxJaw(v);
              setBridge("maxJaw", v);
            }}
          />
          <Slider
            label="Intensity"
            value={intensity}
            min={0.5}
            max={1.0}
            step={0.02}
            decimals={2}
            onChange={(v) => {
              setIntensity(v);
              setBridge("intensity", v);
            }}
          />

          <div style={styles.telemetry}>
            <div>
              outputLatency (device):{" "}
              <b style={{ color: measuredMs == null ? "#ff7a7a" : "#7affb0" }}>
                {measuredMs == null ? "— (0/NaN → manual)" : `${measuredMs.toFixed(1)} ms`}
              </b>
            </div>
            <div>
              phase applied: <b>{effectiveMs == null ? "—" : `${Math.round(effectiveMs)} ms`}</b>
            </div>
          </div>
        </>
      )}
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(panel, document.body);
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  unit,
  decimals = 0,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  decimals?: number;
  onChange: (v: number) => void;
}) {
  return (
    <label style={styles.row}>
      <div style={styles.rowTop}>
        <span>{label}</span>
        <span style={styles.value}>
          {value.toFixed(decimals)}
          {unit ? ` ${unit}` : ""}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={styles.range}
      />
    </label>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    position: "fixed",
    bottom: 16,
    right: 16,
    width: 230,
    zIndex: 2147483000,
    pointerEvents: "auto",
    background: "rgba(10, 10, 15, 0.92)",
    border: "1px solid rgba(0, 229, 255, 0.35)",
    borderRadius: 10,
    padding: "10px 12px",
    color: "#e6f7ff",
    font: "11px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace",
    boxShadow: "0 8px 30px rgba(0,0,0,0.5)",
    backdropFilter: "blur(6px)",
    userSelect: "none",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  title: { color: "#00e5ff", letterSpacing: 0.5, textTransform: "uppercase", fontSize: 10 },
  btn: {
    background: "transparent",
    border: "1px solid rgba(230,247,255,0.25)",
    color: "#e6f7ff",
    borderRadius: 6,
    padding: "1px 7px",
    marginLeft: 6,
    cursor: "pointer",
    font: "inherit",
  },
  row: { display: "block", marginBottom: 9 },
  rowTop: { display: "flex", justifyContent: "space-between", marginBottom: 2 },
  value: { color: "#7affb0" },
  range: { width: "100%", accentColor: "#00e5ff", cursor: "pointer" },
  telemetry: {
    marginTop: 6,
    paddingTop: 6,
    borderTop: "1px solid rgba(230,247,255,0.15)",
    fontSize: 10,
    color: "#a9c7d6",
    display: "grid",
    gap: 2,
  },
};
